import { create } from "zustand";
import {
  AuthError,
  AuthErrorType,
  AuthState,
  createAuthError,
} from "@/lib/types/auth";
import { completeGoogleLogin as apiCompleteGoogleLogin, getCurrentUser } from "@/lib/api/auth";
import {
  setAuthData,
  clearAllAuthData,
  getUserFromStorage,
  getAccessToken,
  checkAuthStatus,
  updateUserData,
} from "../lib/services/auth-storage";

interface AuthStore extends AuthState {
  initializeAuth: () => void;
  checkAuth: () => Promise<void>;
  loginWithGoogle: (code: string, callback_url: string) => Promise<void>;
  logout: () => void;
  refreshUserData: () => Promise<void>;
  hasEntityAction: (entityType: string, action: string | string[]) => boolean;
  isInGroup: (group: string) => boolean;
  clearError: () => void;
}

// Hydrate auth state from localStorage at module load — synchronous, no
// useEffect, no hydration race. The previous design left `user: null /
// isAuthenticated: false` as the initial values and relied on
// AuthWrapper's useEffect to call `initializeAuth()`. But AuthWrapper
// renders inside the router, so by the time its effect fires the router
// has already been built once with the wrong auth state — on a hard
// refresh of a nested page, `createRouter` saw `isAuthenticated: false`
// and registered no app routes, and the URL fell through to the NotFound
// catch-all. Reading from storage right here means the very first
// `useAuthStore()` call already returns the hydrated values, so the
// router builds correctly on first paint.
const HYDRATED_USER = getUserFromStorage();
const HYDRATED_ACCESS = getAccessToken();
const HYDRATED_IS_AUTHENTICATED = !!(HYDRATED_USER && HYDRATED_ACCESS);

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: HYDRATED_USER,
  isLoading: false,
  isAuthenticated: HYDRATED_IS_AUTHENTICATED,
  error: null,

  // Re-hydrate from storage. Still exposed because the cross-tab
  // storage-event listener (further down this file) calls it on
  // external auth changes.
  initializeAuth: () => {
    const user = getUserFromStorage();
    const access = getAccessToken();

    set({
      user,
      isAuthenticated: !!(user && access),
      isLoading: false,
      error: null,
    });
  },

  // Clear any existing errors
  clearError: () => {
    set({ error: null });
  },

  // Check authentication status
  checkAuth: async () => {
    set({ isLoading: true });

    try {
      const user = checkAuthStatus();

      set({
        user,
        isLoading: false,
        isAuthenticated: !!user,
        error: null,
      });
    } catch (error) {
      // Determine error type based on the caught error
      let authError: AuthError;

      if (error instanceof Error && error.message.includes("Network Error")) {
        authError = createAuthError(
          "Network error. Please check your connection.",
          AuthErrorType.NETWORK,
          error
        );
      } else if (error instanceof Error && error.message.includes("401")) {
        authError = createAuthError(
          "Your session has expired. Please log in again.",
          AuthErrorType.EXPIRED_SESSION,
          error
        );
      } else if (error instanceof Error && error.message.includes("500")) {
        authError = createAuthError(
          "Server error. Please try again later.",
          AuthErrorType.SERVER_ERROR,
          error
        );
      } else {
        authError = createAuthError(
          "An unexpected error occurred. Please try again.",
          AuthErrorType.UNKNOWN,
          error
        );
      }

      set({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: authError,
      });
    }
  },

  // Login with Google
  loginWithGoogle: async (code: string, callback_url: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiCompleteGoogleLogin(code, callback_url);

      // Expect response to contain user, access, refresh
      const { user, access, refresh } = response;

      if (!user || !access || !refresh) {
        throw new Error("Invalid response format: missing user or tokens");
      }

      // Store all auth data
      setAuthData(user, access, refresh);

      set({
        user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch (error) {
      // Determine error type based on the caught error
      let authError: AuthError;

      if (error instanceof Error && error.message.includes("Network Error")) {
        authError = createAuthError(
          "Network error during login. Please check your connection and try again.",
          AuthErrorType.NETWORK,
          error
        );
      } else if (
        error instanceof Error &&
        (error.message.includes("invalid_grant") ||
          error.message.includes("401"))
      ) {
        authError = createAuthError(
          "Invalid or expired authentication code. Please try logging in again.",
          AuthErrorType.INVALID_CREDENTIALS,
          error
        );
      } else if (error instanceof Error && error.message.includes("500")) {
        authError = createAuthError(
          "Server error during login. Please try again later.",
          AuthErrorType.SERVER_ERROR,
          error
        );
      } else {
        authError = createAuthError(
          "An unexpected error occurred during login. Please try again.",
          AuthErrorType.UNKNOWN,
          error
        );
      }

      set({
        isLoading: false,
        isAuthenticated: false,
        error: authError,
      });
    }
  },

  // Logout
  logout:  () => {
    set({ isLoading: true });

    try {
      // Clear all local auth data
      clearAllAuthData();

      set({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
    } catch (error) {
      // Even if the API call fails, we still want to logout locally
      clearAllAuthData();

      const authError = createAuthError(
        "Error during logout, but you've been logged out locally.",
        AuthErrorType.UNKNOWN,
        error
      );

      set({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: authError,
      });
    }
  },

  // Refresh user data from server
  refreshUserData: async () => {
    const currentState = get();
    
    // Only refresh if user is authenticated
    if (!currentState.isAuthenticated) {
      return;
    }

    try {
      const freshUser = await getCurrentUser();
      
      // Update storage with fresh user data
      updateUserData(freshUser);
      
      // Update store state
      set({
        user: freshUser,
        error: null,
      });
    } catch (error) {
      // If refresh fails, don't update the user data
      // The error will be handled by the interceptor if it's a 401
      console.warn("Failed to refresh user data:", error);
    }
  },

  // Check if user has specific action permission for entity type
  hasEntityAction: (entityType: string, action: string | string[]): boolean => {
    const user = get().user;
    if (!user?.permissions) return false;

    const actions = Array.isArray(action) ? action : [action];

    return user.permissions.some(
      (permission) =>
        permission.entity.entity_type === entityType &&
        actions.some((act) => permission.actions.includes(act))
    );
  },

  // Group membership check
  isInGroup: (group: string): boolean => {
    return !!get().user?.groups?.includes(group);
  },
}));

// Setup storage event listener to sync auth state across tabs
if (globalThis.window !== undefined) {
  globalThis.window.addEventListener('storage', (e) => {
    // Only handle auth-related storage changes
    if (
      e.key === 'auth_user' ||
      e.key === 'auth_access_token' ||
      e.key === 'auth_refresh_token'
    ) {
      // Re-initialize auth state from storage when it changes in another tab
      useAuthStore.getState().initializeAuth();
    }
  });
}